import { PAGE_LIMIT_MAX, shiftDay, errorWords } from "@alm/domain";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { CalendarPlus, Repeat } from "@/lib/icons";
import { useState } from "react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import type { ColumnDef } from "@tanstack/react-table";
import { DataTable } from "@/components/app/patterns/data-table";
import { OverflowText } from "@/components/app/lists/overflow-text";
import { Skeleton } from "@/components/ui/skeleton";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { orpc } from "@/lib/orpc";
import { optionalOneOf, KEEP_PLACE } from "@/lib/search";
import { useCan } from "@/lib/policy";
import { EmptyState } from "@/components/app/lists/empty-state";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { AppointmentCard } from "@/components/app/appointments/appointment-card";
import { AppointmentMenu } from "@/components/app/appointments/appointment-menu";
import { type AppointmentActions } from "@/components/app/appointments/appointment-card-types";
import { AppointmentEditorDialog } from "@/components/app/appointments/appointment-editor-dialog";
import type { Appointment } from "@/components/app/appointments/appointment-editor-state";
import { CompleteAppointmentDialog } from "@/components/app/appointments/appointment-status-dialogs";
import { CancelAppointmentDialog } from "@/components/app/appointments/cancel-appointment-dialog";
import { DeleteAppointmentDialog } from "@/components/app/appointments/delete-appointment-dialog";
import {
  APPOINTMENT_TYPE_LABELS,
  dayKeyIn,
  formatMiles,
  STATUS_LABELS,
  statusBadgeVariant,
  timeIn,
  type AppointmentStatus,
  type AppointmentType,
} from "@/components/app/appointments/labels";

export const Route = createFileRoute("/_authenticated/_care/residents/$residentId/appointments")({
  validateSearch: (search: Record<string, unknown>): { view?: "all" } => ({
    view: optionalOneOf(["all"] as const, search.view),
  }),
  component: AppointmentsTab,
});

type Dialogs =
  | { kind: "editor"; editing: Appointment | null; duplicating: Appointment | null }
  | { kind: "complete" | "cancel" | "delete"; appointment: Appointment }
  | null;

function dayLabel(key: string, today: string, tomorrow: string) {
  if (key === today) return "Today";
  if (key === tomorrow) return "Tomorrow";
  const [y, m, d] = key.split("-").map(Number);
  return new Date(y!, (m ?? 1) - 1, d ?? 1).toLocaleDateString(undefined, {
    weekday: "long",
    month: "short",
    day: "numeric",
    ...(String(y) === today.slice(0, 4) ? {} : { year: "numeric" }),
  });
}

const cutoff = () => new Date(Date.now() - 86_400_000);

function appointmentColumns(tz: string): ColumnDef<Appointment>[] {
  return [
    {
      id: "date",
      accessorFn: (a) => new Date(a.scheduledStart).getTime(),
      header: "Date",
      meta: { nowrap: true },
      cell: ({ row }) => timeIn(row.original.scheduledStart, tz, true),
    },
    {
      accessorKey: "title",
      header: "Appointment",
      enableHiding: false,
      cell: ({ row }) => (
        <>
          <div className="flex items-center gap-2">
            <OverflowText className="font-medium">{row.original.title}</OverflowText>
            {row.original.seriesId ? <Repeat className="text-muted-foreground size-3" /> : null}
          </div>
          <p className="text-muted-foreground text-xs">
            {APPOINTMENT_TYPE_LABELS[row.original.appointmentType as AppointmentType] ??
              row.original.appointmentType}
          </p>
        </>
      ),
    },
    {
      id: "provider",
      accessorFn: (a) => a.providerName || a.providerNameFree || a.institutionName || "",
      header: "Provider",
      cell: ({ row, getValue }) => (
        <>
          {getValue<string>() ? (
            <OverflowText>{getValue<string>()}</OverflowText>
          ) : (
            <span className="text-muted-foreground block">-</span>
          )}
          {formatMiles(row.original.distanceMiles) ? (
            <span className="text-muted-foreground text-xs">
              {formatMiles(row.original.distanceMiles)}
            </span>
          ) : null}
        </>
      ),
    },
    {
      id: "status",
      accessorFn: (a) => STATUS_LABELS[a.status as AppointmentStatus] ?? a.status,
      header: "Status",
      meta: { filter: "select" },
      cell: ({ row, getValue }) => (
        <Badge variant={statusBadgeVariant(row.original.status)}>{String(getValue())}</Badge>
      ),
    },
    {
      id: "outcome",
      accessorFn: (a) => (a.status === "cancelled" ? a.cancelReason || "-" : a.outcomeNotes || "-"),
      header: "Outcome",
      enableSorting: false,
      meta: { priority: 2, className: "text-muted-foreground", clamp: "lg" },
    },
  ];
}

/** Medical and personal appointments with the transport plan for each. */
function AppointmentsTab() {
  const { residentId } = Route.useParams();
  const { data: resident } = useSuspenseQuery(
    orpc.care.residents.get.queryOptions({ input: { residentId } }),
  );
  const tz = resident.facilityTimezone ?? "UTC";
  const canManage = useCan("appointment:manage", resident.facilityId);
  const queryClient = useQueryClient();
  const navigate = useNavigate({ from: Route.fullPath });
  const view: "upcoming" | "all" = Route.useSearch().view ?? "upcoming";
  const setView = (next: "upcoming" | "all") =>
    void navigate({
      search: { view: next === "all" ? "all" : undefined },
      ...KEEP_PLACE,
    });
  const [dialog, setDialog] = useState<Dialogs>(null);
  const [from] = useState(cutoff);

  const list = useQuery(
    orpc.care.appointments.list.queryOptions({
      input: {
        residentId,
        limit: PAGE_LIMIT_MAX,
        offset: 0,
        ...(view === "upcoming" ? { from } : {}),
      },
    }),
  );
  const patch = useMutation(
    orpc.care.appointments.update.mutationOptions({
      onSuccess: async (_result, variables) => {
        toast.success(
          `Appointment ${STATUS_LABELS[variables.status as AppointmentStatus]?.toLowerCase() ?? "updated"}`,
        );
        await queryClient.invalidateQueries({ queryKey: orpc.care.appointments.key() });
      },
      onError: (error) => toast.error(errorWords(error)),
    }),
  );
  const rows = list.data?.rows ?? [];
  const actions: AppointmentActions = {
    edit: (a) => setDialog({ kind: "editor", editing: a, duplicating: null }),
    duplicate: (a) => setDialog({ kind: "editor", editing: null, duplicating: a }),
    confirm: (a) => patch.mutate({ appointmentId: a.id, status: "confirmed" }),
    complete: (a) => setDialog({ kind: "complete", appointment: a }),
    cancel: (a) => setDialog({ kind: "cancel", appointment: a }),
    noShow: (a) => patch.mutate({ appointmentId: a.id, status: "no_show" }),
    remove: (a) => setDialog({ kind: "delete", appointment: a }),
  };

  const groups = (() => {
    const byDay = new Map<string, Appointment[]>();
    for (const a of rows.toSorted(
      (x, y) => new Date(x.scheduledStart).getTime() - new Date(y.scheduledStart).getTime(),
    )) {
      const key = dayKeyIn(a.scheduledStart, tz);
      byDay.set(key, [...(byDay.get(key) ?? []), a]);
    }
    return [...byDay.entries()].toSorted(([a], [b]) => a.localeCompare(b));
  })();
  const todayKey = dayKeyIn(new Date(), tz);
  const tomorrowKey = shiftDay(todayKey, 1);
  const newestFirst = rows.toSorted(
    (x, y) => new Date(y.scheduledStart).getTime() - new Date(x.scheduledStart).getTime(),
  );
  const create = () => setDialog({ kind: "editor", editing: null, duplicating: null });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold">Appointments</h2>
          <p className="text-muted-foreground text-sm">
            Medical and personal appointments with the transport plan for each
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <ToggleGroup
            type="single"
            variant="outline"
            aria-label="Which appointments"
            value={view}
            onValueChange={(value) => (value ? setView(value as typeof view) : null)}
          >
            <ToggleGroupItem value="upcoming">Upcoming</ToggleGroupItem>
            <ToggleGroupItem value="all">All</ToggleGroupItem>
          </ToggleGroup>
          {canManage ? (
            <Button onClick={create}>
              <CalendarPlus className="size-4" />
              Add appointment
            </Button>
          ) : null}
        </div>
      </div>

      {list.isPending ? (
        <div className="space-y-3">
          <Skeleton className="h-20" />
          <Skeleton className="h-20" />
          <Skeleton className="h-20" />
        </div>
      ) : list.isError ? (
        <ReadFailed
          message="Could not load this resident's appointments."
          error={list.error}
          onRetry={() => void list.refetch()}
          retrying={list.isFetching}
        />
      ) : rows.length === 0 ? (
        <Card className="py-0">
          <EmptyState
            title={view === "upcoming" ? "No upcoming appointments" : "No appointments yet"}
            description="Schedule medical visits, therapy, outings, and more, with the transport plan built in."
          />
        </Card>
      ) : view === "upcoming" ? (
        <div className="space-y-5">
          {groups.map(([key, items]) => (
            <div key={key} className="space-y-2">
              <h3 className="text-muted-foreground text-sm font-medium">
                {dayLabel(key, todayKey, tomorrowKey)}
              </h3>
              <div className="space-y-2">
                {items.map((a) => (
                  <AppointmentCard
                    key={a.id}
                    a={a}
                    tz={tz}
                    canManage={canManage}
                    actions={actions}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <DataTable
          quiet
          data={newestFirst}
          columns={appointmentColumns(tz)}
          noun="appointments"
          rowId={(a) => a.id}
          renderRowActions={
            canManage ? (a) => <AppointmentMenu appointment={a} actions={actions} /> : undefined
          }
        />
      )}

      {dialog?.kind === "editor" ? (
        <AppointmentEditorDialog
          residentId={residentId}
          facilityId={resident.facilityId}
          zone={tz}
          editing={dialog.editing}
          duplicating={dialog.duplicating}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {dialog?.kind === "complete" ? (
        <CompleteAppointmentDialog
          appointment={dialog.appointment}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {dialog?.kind === "cancel" ? (
        <CancelAppointmentDialog appointment={dialog.appointment} onClose={() => setDialog(null)} />
      ) : null}
      {dialog?.kind === "delete" ? (
        <DeleteAppointmentDialog appointment={dialog.appointment} onClose={() => setDialog(null)} />
      ) : null}
    </div>
  );
}
